import { StorageReference } from 'firebase/storage'
import { validate as isValidUUID } from 'uuid'

/**
 * Extracts relevant path segments from a full storage path,
 * skipping the "users" prefix and user ID
 *
 * @param fullPath - Full storage path (e.g., "users/uid/matterId/subfolder")
 * @returns Array of relevant segments (e.g., ["matterId", "subfolder"])
 */
export const getRelevantSegments = (fullPath: string): string[] => {
  const segments = fullPath.split('/').filter(Boolean)

  if (segments.length <= 2) {
    // nothing after users + uid
    return []
  }

  return segments.slice(2)
}

/**
 * Renders a folder name with proper matter name mapping
 * Converts matter UUIDs to their human-readable names
 *
 * @param name - Folder name (potentially a UUID)
 * @param folderRef - Firebase storage reference to the folder
 * @param renderedMatterNameMap - Map of matter IDs to display names
 * @returns Rendered folder name (either mapped name or original)
 */
export function renderFolderName(name: string, folderRef: StorageReference, renderedMatterNameMap: Map<string, string>): string {
  const isUserRootFolder = getRelevantSegments(folderRef.fullPath).length === 1
  if (isUserRootFolder) {
    const renderedNameFromMap = renderedMatterNameMap.get(name)
    return renderedNameFromMap || name
  }
  return name
}

/**
 * Checks if a folder path is exactly at the matter folder root level AND is a real matter
 * Returns true only if path has exactly 1 segment that is both a valid UUID AND exists in validMatterIds
 *
 * @param fullPath - Full storage path (e.g., "users/uid/matterId")
 * @param validMatterIds - Set of valid matter IDs from the store
 * @returns true only if the folder is a real matter folder root
 */
export function isRootMatterFolder(fullPath: string, validMatterIds: Set<string>): boolean {
  const relevantSegments = getRelevantSegments(fullPath)
  if (relevantSegments.length !== 1) {
    return false
  }
  const potentialMatterId = relevantSegments[0]
  return isValidUUID(potentialMatterId) && validMatterIds.has(potentialMatterId)
}

/**
 * Checks if a folder path is inside a matter folder tree (at any depth)
 * Returns true if the first segment is a valid UUID AND exists in validMatterIds
 *
 * @param fullPath - Full storage path (e.g., "users/uid/matterId/subfolder")
 * @param validMatterIds - Set of valid matter IDs from the store
 * @returns true if the path is anywhere within a matter folder tree
 */
export function isInsideMatterFolder(fullPath: string, validMatterIds: Set<string>): boolean {
  const relevantSegments = getRelevantSegments(fullPath)
  if (relevantSegments.length < 1) {
    return false // At drive root
  }
  const potentialMatterId = relevantSegments[0]
  return isValidUUID(potentialMatterId) && validMatterIds.has(potentialMatterId)
}
